import { BASE_SERVICE_URL } from '@/lib/config';

import {
  BlogPublicArchive,
  BlogPublicHub,
  BlogPublicPost,
  SpringPage,
} from '../model/types';

const BASE_BLOG_URL = `${BASE_SERVICE_URL}/blog`;

const fetchJson = async <T>(url: string): Promise<T | null> => {
  const response = await fetch(url);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch blog. Status: ${response.status}`);
  }

  return (await response.json()) as T;
};

export const fetchBlogHub = async (
  lang: string
): Promise<BlogPublicHub | null> => {
  const hub = await fetchJson<BlogPublicHub>(`${BASE_BLOG_URL}?lang=${lang}`);

  if (!hub) {
    return null;
  }

  return {
    ...hub,
    tags: hub.tags ?? [],
  };
};

export const fetchBlogPost = async (
  categorySlug: string,
  id: number | string,
  lang: string
): Promise<BlogPublicPost | null> => {
  return fetchJson<BlogPublicPost>(
    `${BASE_BLOG_URL}/${encodeURIComponent(categorySlug)}/${id}?lang=${lang}`
  );
};

export const fetchBlogCategory = async (
  slug: string,
  lang: string,
  apiPage: number
): Promise<SpringPage<BlogPublicPost> | null> => {
  return fetchJson<SpringPage<BlogPublicPost>>(
    `${BASE_BLOG_URL}/categories/${encodeURIComponent(slug)}?lang=${lang}&page=${apiPage}`
  );
};

export const fetchBlogTags = async (
  tag: string,
  lang: string,
  apiPage: number
): Promise<SpringPage<BlogPublicPost> | null> => {
  return fetchJson<SpringPage<BlogPublicPost>>(
    `${BASE_BLOG_URL}/tags?tag=${encodeURIComponent(tag)}&lang=${lang}&page=${apiPage}`
  );
};

export const fetchBlogSearch = async (
  query: string,
  lang: string,
  apiPage: number
): Promise<SpringPage<BlogPublicPost> | null> => {
  return fetchJson<SpringPage<BlogPublicPost>>(
    `${BASE_BLOG_URL}/search?query=${encodeURIComponent(query)}&lang=${lang}&page=${apiPage}`
  );
};

export const fetchBlogLatest = async (
  lang: string,
  apiPage: number
): Promise<SpringPage<BlogPublicPost> | null> => {
  return fetchJson<SpringPage<BlogPublicPost>>(
    `${BASE_BLOG_URL}/latest?lang=${lang}&page=${apiPage}`
  );
};

export const fetchBlogArchive = async (
  lang: string
): Promise<BlogPublicArchive | null> => {
  return fetchJson<BlogPublicArchive>(`${BASE_BLOG_URL}/archive?lang=${lang}`);
};

export const trackBlogPostView = async (id: number | string): Promise<void> => {
  try {
    await fetch(`${BASE_BLOG_URL}/${id}/view`, { method: 'POST' });
  } catch {
    return;
  }
};
